import {
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useEncryption } from '@/hooks/useEncryption';
import { useOptionalManuscriptContext } from '@/contexts/ManuscriptContext';
import { useQueryClient } from '@tanstack/react-query';
import { useRepository } from '@/contexts/PlatformContext';
import { processPayload } from '@/lib/processPayload';
import {
  gatherBackupPayload,
  type ManuscriptBackupPayload,
} from '@/lib/backupPayload';
import { createConflictCopy } from '@/lib/conflictCopier';
import {
  getSha256,
  stringifyCanonicalJson,
} from '@/utils/hash';
import { manuscriptKeys } from '@/hooks/useManuscripts';
import { toast } from 'sonner';
import { withLock } from '@/utils/lock';
import { logger } from '@/lib/logger';
import { isWebCompanionPlatform } from '@/utils/platform';
import { webRepository } from '@/lib/repository/webRepository';
import {
  computeEntityHashes,
  smartMergePayloads,
} from '@/lib/smartMerge';
import { getEntityHashesKey } from '@/utils/storageKeys';
import {
  getLastSyncedRevision,
  setLastSyncedRevision,
  setLastSyncedBaseHashes,
  getLastSyncedBaseHashes,
} from '@/lib/revisionedSync';

export interface SyncResult {
  pulled: number;
  created: number;
  unchanged: number;
  errors: string[];
}

interface SyncAwareRepository {
  recordMutation?: (
    manuscriptId: string
  ) => Promise<void>;
  flushPendingWrites?: (
    manuscriptId?: string
  ) => Promise<void>;
  applyRemoteTombstone?: (
    manuscriptId: string
  ) => Promise<void>;
}

const SYNC_COOLDOWN_MS = 30_000;
const SYNC_INTERVAL_MS = 2 * 60 * 1000;

let isSyncingGlobal = false;
let lastSyncTimeGlobal = 0;

export function useCloudSync() {
  const { user } = useAuth();
  const {
    isReady: encryptionReady,
    decryptContent,
  } = useEncryption();
  const manuscriptContext =
    useOptionalManuscriptContext();
  const queryClient = useQueryClient();
  const repository = useRepository();

  const [isSyncing, setIsSyncing] =
    useState(false);
  const [lastSyncTime, setLastSyncTime] =
    useState<Date | null>(null);
  const [lastSyncResult, setLastSyncResult] =
    useState<SyncResult | null>(null);

  const isSyncingRef = useRef(false);
  const lastSyncTimeRef = useRef(0);
  const initialSyncDoneRef = useRef(false);

  const syncFromCloud =
    useCallback(async (): Promise<SyncResult> => {
      const result: SyncResult = {
        pulled: 0,
        created: 0,
        unchanged: 0,
        errors: [],
      };

      if (!user?.id) return result;

      if (isWebCompanionPlatform()) {
        try {
          await webRepository.refreshCache();
          queryClient.invalidateQueries({
            queryKey: manuscriptKeys.all,
          });
          setLastSyncTime(new Date());
        } catch (err) {
          console.error(
            '[CloudSync Web] Refresh cache failed:',
            err
          );
        }

        return result;
      }

      if (!encryptionReady) return result;

      if (
        isSyncingGlobal ||
        isSyncingRef.current
      ) {
        return result;
      }

      const now = Date.now();

      if (
        now - lastSyncTimeGlobal <
          SYNC_COOLDOWN_MS ||
        now - lastSyncTimeRef.current <
          SYNC_COOLDOWN_MS
      ) {
        logger.log(
          '[CloudSync] Skipping because cooldown is active.'
        );
        return result;
      }

      isSyncingGlobal = true;
      isSyncingRef.current = true;
      setIsSyncing(true);

      const syncAwareRepository =
        repository as typeof repository &
          SyncAwareRepository;

      const applyRemoteDeletion = async (
        manuscriptId: string
      ): Promise<void> => {
        if (
          syncAwareRepository.applyRemoteTombstone
        ) {
          await syncAwareRepository.applyRemoteTombstone(
            manuscriptId
          );
        } else {
          await repository.deleteManuscript(
            manuscriptId,
            false
          );
        }
      };

      try {
        const lockResult = await withLock(
          'deckle_cloud_sync_lock',
          async () => {
            const {
              data: cloudIndex,
              error: indexError,
            } = await supabase
              .from('manuscript_heads')
              .select(
                'manuscript_id, revision, updated_at, deleted_at'
              )
              .eq('user_id', user.id);

            if (indexError) {
              console.error(
                '[CloudSync] Failed to fetch cloud head index:',
                indexError
              );
              result.errors.push(
                indexError.message
              );
              return result;
            }

            const activeManuscriptId =
              manuscriptContext?.manuscriptId ??
              null;

            const cloudMap = new Map<
              string,
              {
                revision: number;
                updatedAt: string;
                deletedAt: string | null;
              }
            >();

            if (cloudIndex) {
              for (const row of cloudIndex) {
                if (
                  row.manuscript_id ===
                  '__verification__'
                ) {
                  continue;
                }

                cloudMap.set(row.manuscript_id, {
                  revision: Number(
                    row.revision ?? 0
                  ),
                  updatedAt: row.updated_at,
                  deletedAt: row.deleted_at,
                });
              }
            }

            const manuscriptsToFetch: string[] =
              [];
            const locallyChangedManuscripts =
              new Map<
                string,
                ManuscriptBackupPayload
              >();

            for (const [
              manuscriptId,
              cloudHead,
            ] of cloudMap) {
              if (
                manuscriptId === activeManuscriptId
              ) {
                result.unchanged += 1;
                continue;
              }

              if (cloudHead.deletedAt) {
                const localManuscript =
                  await repository.getManuscript(
                    manuscriptId
                  );

                if (localManuscript) {
                  logger.log(
                    `[CloudSync] Applying remote tombstone for ${manuscriptId}.`
                  );
                  await applyRemoteDeletion(
                    manuscriptId
                  );
                  toast.info(
                    `"${localManuscript.title}" deleted on another device`
                  );
                }

                continue;
              }

              if (
                repository.hasPendingWrites?.(
                  manuscriptId
                )
              ) {
                locallyChangedManuscripts.set(
                  manuscriptId,
                  await gatherBackupPayload(
                    manuscriptId,
                    repository
                  )
                );
              }

              const localRevision =
                getLastSyncedRevision(
                  manuscriptId
                );

              if (
                localRevision <
                cloudHead.revision
              ) {
                const localManuscript =
                  await repository.getManuscript(
                    manuscriptId
                  );

                if (!localManuscript) {
                  manuscriptsToFetch.push(
                    manuscriptId
                  );
                  continue;
                }

                const localPayload =
                  await gatherBackupPayload(
                    manuscriptId,
                    repository
                  );
                const localHash =
                  await getSha256(
                    stringifyCanonicalJson(
                      localPayload
                    )
                  );
                const lastSyncedHash =
                  localStorage.getItem(
                    `deckle_last_synced_hash_${manuscriptId}`
                  );

                if (
                  localHash !== lastSyncedHash
                ) {
                  locallyChangedManuscripts.set(
                    manuscriptId,
                    localPayload
                  );
                }

                manuscriptsToFetch.push(
                  manuscriptId
                );
              } else {
                result.unchanged += 1;
              }
            }

            const localManuscripts =
              await repository.getAllManuscripts();
            let deletionOccurred = false;

            for (const localManuscript of localManuscripts) {
              if (
                cloudMap.has(localManuscript.id)
              ) {
                continue;
              }

              const lastSynced =
                localStorage.getItem(
                  `deckle_last_synced_${localManuscript.id}`
                );

              if (!lastSynced) continue;

              if (
                localManuscript.id ===
                activeManuscriptId
              ) {
                localStorage.removeItem(
                  `deckle_last_synced_${localManuscript.id}`
                );
                localStorage.removeItem(
                  `deckle_last_synced_hash_${localManuscript.id}`
                );
                toast.info(
                  `"${localManuscript.title}" was removed remotely but was preserved because it is currently being edited.`
                );
                continue;
              }

              await applyRemoteDeletion(
                localManuscript.id
              );

              toast.info(
                `"${localManuscript.title}" deleted on another device`
              );
              deletionOccurred = true;
            }

            if (
              manuscriptsToFetch.length === 0
            ) {
              if (deletionOccurred) {
                queryClient.invalidateQueries({
                  queryKey: manuscriptKeys.all,
                });
              }

              return result;
            }

            const {
              data: fullRows,
              error: fetchError,
            } = await supabase
              .from('manuscript_heads')
              .select(
                'manuscript_id, revision, payload, is_encrypted, updated_at, deleted_at'
              )
              .eq('user_id', user.id)
              .in(
                'manuscript_id',
                manuscriptsToFetch
              );

            if (fetchError || !fullRows) {
              console.error(
                '[CloudSync] Failed to fetch manuscript heads:',
                fetchError
              );
              result.errors.push(
                fetchError?.message ??
                  'Failed to fetch manuscript heads'
              );
              return result;
            }

            const pulledTitles: string[] = [];

            for (const row of fullRows) {
              try {
                if (row.deleted_at) {
                  continue;
                }

                let payloadString = row.payload;

                if (row.is_encrypted) {
                  const decrypted =
                    await decryptContent(
                      row.payload
                    );

                  if (!decrypted) {
                    result.errors.push(
                      `Failed to decrypt manuscript ${row.manuscript_id}`
                    );
                    continue;
                  }

                  payloadString = decrypted;
                }

                const cloudPayload =
                  JSON.parse(
                    payloadString
                  ) as ManuscriptBackupPayload;
                const existed =
                  await repository.getManuscript(
                    row.manuscript_id
                  );

                let payloadToProcess =
                  cloudPayload;
                const hasLocalChanges =
                  locallyChangedManuscripts.has(
                    row.manuscript_id
                  );

                if (hasLocalChanges) {
                  const localPayload =
                    locallyChangedManuscripts.get(
                      row.manuscript_id
                    )!;
                  const syncedHashes =
                    getLastSyncedBaseHashes(
                      row.manuscript_id
                    ) ?? {
                      scenes: {},
                      codex: {},
                    };

                  const mergeResult =
                    await smartMergePayloads(
                      localPayload,
                      cloudPayload,
                      syncedHashes
                    );

                  if (
                    mergeResult.hasConflicts
                  ) {
                    try {
                      const conflictCopyId =
                        await createConflictCopy(
                          row.manuscript_id,
                          repository
                        );
                      const original =
                        await repository.getManuscript(
                          row.manuscript_id
                        );

                      toast.warning(
                        `Conflict in "${original?.title ?? 'Manuscript'}". Local changes were saved as a copy.`
                      );

                      localStorage.removeItem(
                        `deckle_last_synced_${conflictCopyId}`
                      );
                      localStorage.removeItem(
                        `deckle_last_synced_hash_${conflictCopyId}`
                      );
                      localStorage.removeItem(
                        getEntityHashesKey(
                          conflictCopyId
                        )
                      );
                    } catch (copyErr) {
                      throw new Error(
                        `Conflict copy creation failed: ${
                          copyErr instanceof Error
                            ? copyErr.message
                            : String(copyErr)
                        }`
                      );
                    }
                  } else {
                    toast.info(
                      `Changes merged automatically for "${mergeResult.merged.manuscript?.title ?? 'Manuscript'}"`
                    );
                  }

                  payloadToProcess =
                    mergeResult.merged;
                }

                await processPayload(
                  payloadToProcess,
                  repository
                );

                if (hasLocalChanges) {
                  setLastSyncedRevision(
                    row.manuscript_id,
                    Number(row.revision)
                  );

                  if (
                    syncAwareRepository.recordMutation
                  ) {
                    await syncAwareRepository.recordMutation(
                      row.manuscript_id
                    );
                  }

                  if (
                    syncAwareRepository.flushPendingWrites
                  ) {
                    await syncAwareRepository.flushPendingWrites(
                      row.manuscript_id
                    );
                  }
                } else {
                  const pulledHash =
                    await getSha256(
                      stringifyCanonicalJson(
                        payloadToProcess
                      )
                    );
                  const entityHashes =
                    await computeEntityHashes(
                      payloadToProcess
                    );

                  setLastSyncedRevision(
                    row.manuscript_id,
                    Number(row.revision)
                  );
                  setLastSyncedBaseHashes(
                    row.manuscript_id,
                    entityHashes
                  );

                  localStorage.setItem(
                    `deckle_last_synced_${row.manuscript_id}`,
                    new Date(
                      row.updated_at
                    ).toISOString()
                  );
                  localStorage.setItem(
                    `deckle_last_synced_hash_${row.manuscript_id}`,
                    pulledHash
                  );
                  localStorage.setItem(
                    getEntityHashesKey(
                      row.manuscript_id
                    ),
                    JSON.stringify(
                      entityHashes
                    )
                  );
                }

                if (existed) {
                  result.pulled += 1;
                } else {
                  result.created += 1;
                }

                if (
                  payloadToProcess.manuscript
                    ?.title
                ) {
                  pulledTitles.push(
                    payloadToProcess.manuscript
                      .title
                  );
                }
              } catch (err) {
                const message =
                  err instanceof Error
                    ? err.message
                    : String(err);

                console.error(
                  `[CloudSync] Error processing ${row.manuscript_id}:`,
                  err
                );
                result.errors.push(
                  `${row.manuscript_id}: ${message}`
                );
              }
            }

            if (
              result.pulled > 0 ||
              result.created > 0 ||
              deletionOccurred
            ) {
              queryClient.invalidateQueries({
                queryKey: manuscriptKeys.all,
              });

              const totalUpdated =
                result.pulled + result.created;

              if (
                totalUpdated === 1 &&
                pulledTitles.length > 0
              ) {
                toast.info(
                  `"${pulledTitles[0]}" synced from another device`
                );
              } else if (totalUpdated > 1) {
                toast.info(
                  `${totalUpdated} manuscripts synced from another device`
                );
              }
            }

            return result;
          }
        );

        return lockResult?.result ?? result;
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : String(err);

        console.error(
          '[CloudSync] Sync failed:',
          err
        );
        result.errors.push(message);
        return result;
      } finally {
        isSyncingGlobal = false;
        isSyncingRef.current = false;
        setIsSyncing(false);

        const completedAt = Date.now();
        lastSyncTimeGlobal = completedAt;
        lastSyncTimeRef.current = completedAt;

        setLastSyncTime(new Date());
        setLastSyncResult(result);
      }
    }, [
      user?.id,
      encryptionReady,
      decryptContent,
      manuscriptContext,
      queryClient,
      repository,
    ]);

  const syncFromCloudRef =
    useRef(syncFromCloud);
  syncFromCloudRef.current = syncFromCloud;

  useEffect(() => {
    if (
      !user?.id ||
      initialSyncDoneRef.current
    ) {
      return;
    }

    if (
      !isWebCompanionPlatform() &&
      !encryptionReady
    ) {
      return;
    }

    initialSyncDoneRef.current = true;
    void syncFromCloudRef.current();
  }, [user?.id, encryptionReady]);

  useEffect(() => {
    initialSyncDoneRef.current = false;
  }, [user?.id]);

  useEffect(() => {
    if (
      !user?.id ||
      isWebCompanionPlatform()
    ) {
      return;
    }

    const handleVisibilityChange = () => {
      if (
        document.visibilityState === 'visible'
      ) {
        void syncFromCloudRef.current();
      }
    };

    document.addEventListener(
      'visibilitychange',
      handleVisibilityChange
    );

    return () =>
      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange
      );
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;

    const intervalId = setInterval(() => {
      void syncFromCloudRef.current();
    }, SYNC_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [user?.id]);

  useEffect(() => {
    if (
      !user?.id ||
      isWebCompanionPlatform()
    ) {
      return;
    }

    const handleOnline = () => {
      void syncFromCloudRef.current();
    };

    window.addEventListener(
      'online',
      handleOnline
    );

    return () =>
      window.removeEventListener(
        'online',
        handleOnline
      );
  }, [user?.id]);

  return {
    syncFromCloud,
    isSyncing,
    lastSyncTime,
    lastSyncResult,
  };
}
